import type { NutritionRule, TabConfig } from '@mdev/common';
import comprehensiveRules from './rules/comprehensive_rules.json';
import methylationRules from './rules/methylation.json';
import oneCarbon from './rules/one_carbon.json';
import vitaminD from './rules/vitamin_d.json';
import vitaminC from './rules/vitamin_c.json';
import mineralMetabolism from './rules/mineral_metabolism.json';
import pufas from './rules/pufas.json';
import detox from './rules/detox.json';
import oxidative from './rules/oxidative.json';
import giTolerance from './rules/gi_tolerance.json';
import metabolic from './rules/metabolic.json';
import alcoholCaffeine from './rules/alcohol_caffeine.json';

type RuleSet = { [gene: string]: { [rsid: string]: NutritionRule } };
type ComprehensiveRules = { [tabId: string]: RuleSet };

const comprehensive = comprehensiveRules as unknown as ComprehensiveRules;

const cast = (rules: unknown): RuleSet => rules as RuleSet;

const fallbackRules: Array<RuleSet> = [
  cast(methylationRules),
  cast(oneCarbon),
  cast(vitaminD),
  cast(vitaminC),
  cast(mineralMetabolism),
  cast(pufas),
  cast(detox),
  cast(oxidative),
  cast(giTolerance),
  cast(metabolic),
  cast(alcoholCaffeine),
];

export const buildTabConfigs = (): TabConfig[] => [
  { id: 'methylation', name: 'Methylation', rules: cast(methylationRules) },
  { id: 'detox', name: 'Detox (Phase I/II/III)', rules: cast(detox) },
  {
    id: 'vitamin-d',
    name: 'Vitamin D Pathway',
    rules: comprehensive.vitamin_d ?? cast(vitaminD),
  },
  {
    id: 'one-carbon',
    name: 'B12/Folate/One-Carbon & Choline',
    rules: comprehensive.b12_folate_choline ?? cast(oneCarbon),
  },
  {
    id: 'vitamin-c',
    name: 'Vitamin C Transport',
    rules: comprehensive.vitamin_c ?? cast(vitaminC),
  },
  {
    id: 'mineral-metabolism',
    name: 'Mineral Metabolism (Iron/Ca/Zn)',
    rules: comprehensive.minerals ?? cast(mineralMetabolism),
  },
  {
    id: 'pufas',
    name: 'PUFAs & Lipids',
    rules: comprehensive.lipids_pufas ?? cast(pufas),
  },
  {
    id: 'gi-tolerance',
    name: 'GI Tolerance & Immune Tags',
    rules: comprehensive.gi_tolerance ?? cast(giTolerance),
  },
  {
    id: 'oxidative',
    name: 'Oxidative Stress & Antioxidants',
    rules: comprehensive.oxidative ?? cast(oxidative),
  },
  {
    id: 'metabolic',
    name: 'Energy & Mitochondrial / Metabolic Context',
    rules: comprehensive.mito_metabolic ?? cast(metabolic),
  },
  {
    id: 'alcohol-caffeine',
    name: 'Alcohol & Caffeine Metabolism',
    rules: comprehensive.alcohol_caffeine ?? cast(alcoholCaffeine),
  },
];

export const collectTargetRsids = (): string[] => {
  const rsids = new Set<string>();
  const collectFromRuleSet = (ruleSet: RuleSet) => {
    for (const geneRules of Object.values(ruleSet)) {
      for (const [primaryRsid, rule] of Object.entries(geneRules)) {
        rsids.add(primaryRsid);
        rule.proxies?.forEach((p) => rsids.add(p));
      }
    }
  };

  fallbackRules.forEach(collectFromRuleSet);
  for (const tabRules of Object.values(comprehensive)) {
    collectFromRuleSet(tabRules);
  }

  return Array.from(rsids);
};
